import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import Box from '@mui/material/Box';

import TwDialog from '../../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import TwSelect, { TwSelectOption } from '../../../components/TwSelects/TwSelects';
import useNotifications from '../../../providers/Notifications/Notifications.provider';

import { useAddEquipmentMutation } from './../../../redux/api/equipment/equipmentApi';

import './CreateEquipment.scss';
import {
  CreateEquipmentParams,
  Equipment,
  EquipmentType
} from '../../../redux/api/equipment/equipment.model';

import { useGetConnectionsQuery, useGetAssetIdsQuery } from '../../../redux/api/eld/eldApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import useAuth from '../../../providers/Auth/Auth.provider';

export interface CreateEquipmentProps {
  open: boolean;
  defaultType?: EquipmentType;
  onClose: () => void;
  onSuccess?: (newEquipment: Equipment) => void;
  fromDocManager?: boolean;
}

interface CreateEquipmentFormValues {
  equipmentName: string;
  equipmentType: EquipmentType;
  eldIntegration?: string;
  eldAssetId?: string;
}

const DEFAULT_OPTIONS: TwSelectOption[] = [
  { label: 'Truck', value: EquipmentType.truck },
  { label: 'Trailer', value: EquipmentType.trailer }
];

const initialValues: CreateEquipmentFormValues = {
  equipmentName: '',
  equipmentType: EquipmentType.truck
};

const CreateEquipment: React.FC<CreateEquipmentProps> = ({
  open,
  defaultType,
  onClose,
  onSuccess,
  fromDocManager = false
}): JSX.Element => {
  const { data: authData } = useAuth();

  const hasEldConnections = useMemo<boolean>(() => {
    return authData.userData?.integrations?.eldConnections || false;
  }, [authData.userData]);

  const { enableLiveTracking } = useFeatureFlags();
  const [options, setOptions] = useState<TwSelectOption[]>([]);
  const [connectionsOptions, setConnectionsOptions] = useState<TwSelectOption[]>([]);
  const [assetIdsOptions, setAssetIdsOptions] = useState<TwSelectOption[]>([]);

  const [addEquipment] = useAddEquipmentMutation();
  const { addNotification } = useNotifications();

  const { data: connections = { content: [], total: 0 } } = useGetConnectionsQuery({});

  useEffect(() => {
    if (connections.content && connections.content.length > 0) {
      const options = connections.content.map((connection) => ({
        label: connection.name,
        value: connection.id
      }));
      setConnectionsOptions(options);
    }
  }, [connections]);

  const { values, handleChange, handleSubmit, resetForm, setFieldValue } = useFormik({
    initialValues: initialValues,
    onSubmit: (values: CreateEquipmentFormValues): void => {
      if (!values.equipmentName.trim() || !values.equipmentType) {
        addNotification({
          message: 'You must provide an equipment name or number.',
          variant: 'error',
          position: 'bottomRight',
          timeToShow: 3000
        });
        return;
      }

      submitEquipment({
        ...values,
        equipmentName: values.equipmentName.trim()
      });

      resetForm({
        values: initialValues
      });
    }
  });

  const { data: assetIds = { content: [], total: 0 } } = useGetAssetIdsQuery(
    values && values.eldIntegration
      ? {
          connectionId: values.eldIntegration
        }
      : skipToken
  );

  useEffect(() => {
    if (assetIds.content && assetIds.content.length > 0) {
      const options = assetIds.content.map((assetId) => ({
        label: assetId.value,
        value: assetId.value
      }));
      setAssetIdsOptions(options);
    }
  }, [assetIds]);

  useEffect(() => {
    if (!open) return;

    if (defaultType === EquipmentType.truck) {
      setOptions([{ label: 'Truck', value: EquipmentType.truck }]);
      setFieldValue('equipmentType', EquipmentType.truck);
      return;
    }

    if (defaultType === EquipmentType.trailer) {
      setOptions([{ label: 'Trailer', value: EquipmentType.trailer }]);
      setFieldValue('equipmentType', EquipmentType.trailer);
      return;
    }

    setOptions(DEFAULT_OPTIONS);
  }, [open]);

  const submitEquipment = (values: CreateEquipmentFormValues): void => {
    // if values.eldAssetId is defined, find value in assetIds.content and get key
    const assetId = values.eldAssetId
      ? assetIds.content.find((asset) => asset.value === values.eldAssetId)
      : undefined;

    const params: CreateEquipmentParams = {
      name: values.equipmentName,
      type: values.equipmentType,
      eld_connection_id: values.eldIntegration,
      asset_eld_id: values.eldAssetId,
      asset_eld_id_key: assetId?.key
    };

    addEquipment(params)
      .unwrap()
      .then((payload) => {
        onSuccess && onSuccess(payload);
        onClose();
        addNotification({
          message: 'Equipment created successfully',
          variant: 'success',
          position: 'bottomRight',
          timeToShow: 3000
        });
      })
      .catch((error) => {
        addNotification({
          message: error.data.message,
          variant: 'error',
          position: 'bottomRight',
          timeToShow: 3000
        });
      });
  };

  const handleClose = (): void => {
    onClose();
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <TwDialog
        open={open}
        onClickClose={handleClose}
        size="sm"
        title="New equipment"
        actions={[
          <TwButton
            key="1"
            fullwidth={false}
            type={TwButtonType.secondary}
            label="Cancel"
            action={handleClose}
          />,
          <TwButton
            key="2"
            fullwidth={false}
            type={TwButtonType.primary}
            label="Create"
            action={handleSubmit}
          />
        ]}
        draggable
        sx={{
          '& .MuiDialogContent-root': { padding: '35px 20px !important' },
          ...(fromDocManager && {
            '& .MuiDialog-root': {
              zIndex: '1300 !important'
            }
          })
        }}
      >
        <div className="create-equipment__wrapper tid-create-equipment__wrapper">
          <div className="create-equipment__form-section">
            <div className="create-equipment__inputs-container">
              <div className="create-equipment__two-column">
                <div
                  className="tid-create-equipment__name
                        create-equipment__two-column-item"
                >
                  <TwField
                    id="equipmentName"
                    name="equipmentName"
                    value={values.equipmentName}
                    label="Equipment name or number"
                    inputType={TwInputTypes.text}
                    disabled={false}
                    onInputChange={handleChange}
                    error={false}
                  />
                </div>

                <div
                  className="tid-create-equipment__type
                        create-equipment__two-column-item"
                >
                  <TwSelect
                    id="equipmentType"
                    name="equipmentType"
                    label="Type"
                    value={values.equipmentType}
                    optionList={options}
                    onInputChange={handleChange}
                    disabled={!!defaultType}
                    error={false}
                  />
                </div>
              </div>

              {enableLiveTracking &&
                hasEldConnections &&
                values.equipmentType === EquipmentType.truck && (
                  <div className="create-equipment__two-column">
                    <div
                      className="tid-create-equipment__name
                        create-equipment__two-column-item"
                    >
                      <TwSelect
                        id="eldIntegration"
                        name="eldIntegration"
                        label="ELD integration"
                        value={values.eldIntegration}
                        optionList={connectionsOptions}
                        onInputChange={handleChange}
                        error={false}
                      />
                    </div>

                    {values.eldIntegration && assetIdsOptions.length > 0 && (
                      <div
                        className="tid-create-equipment__type
                        create-equipment__two-column-item"
                      >
                        <TwSelect
                          id="eldAssetId"
                          name="eldAssetId"
                          label="ELD asset ID"
                          value={values.eldAssetId}
                          optionList={assetIdsOptions}
                          onInputChange={handleChange}
                          disabled={!values.eldIntegration || assetIdsOptions.length === 0}
                          error={false}
                        />
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      </TwDialog>
    </Box>
  );
};

export default CreateEquipment;
